<template>
    <div class="sc__analysis__tab__box tab-content">
        <div class="sc__analysis__head__box">
            <div class="title__box">
                <div class="head__title__group">
                    <template v-if="type === 'A'">
                        <h3 class="title">Starting Targets - Syllable Complexity</h3>
                        <p>Based on the information form</p>
                    </template>
                    <h3 class="title" v-else>Starting Targets - Length of Syllable Combinations</h3>
                </div>
                <div class="print__data__btn">
                    <WorkpacketPdf :button-label="`Download`" :assessmentId="assessmentId"
                        :organizationId="organizationId" :learnerId="learnerId"
                        v-if="assessmentId && organizationId && learnerId" />
                </div>
            </div>
        </div>
        <div class="sc__analysis__data__box syllable__complexity__wrapper">
            <div class="tabs__data__wrapper">
                <template v-if="type === 'A'">
                    <div v-for="group in groups" :key="group.id" class="syllable__complexity__group__box"
                        :class="{ active__syllable__complexity__box: groupTargetActivation[group.id] }">
                        
                        <h6 class="group__title">Group {{ group.id }}</h6>
                        <div class="divider__sepline"></div>
                        <div class="checkbox__group__wrapper">
                            <div class="input__checkbox" v-for="label in group.labels" :key="label">
                                <span class="group__head">{{ label.label }}</span>
                                <div class="static__value__wrapper">
                                    <span :class="[group.color, 'static__value']">{{
                                        scvAverages?.[group.id]?.[label.key] ?? 0 }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </template>
                <template v-else>
                    <div class="syllable__combinations__group__wrapper"
                        :class="{ disabled_tab: !(groupTargetActivation[4] || groupTargetActivation[5]) }">
                        <h4 class="title">Group 4-5</h4>
                        <div class="divider__sepline"></div>
                        <div class="syllable__range__combo__wrapper">
                            <h6 class="title">Syllable Range Combo</h6>
                            <div class="range__combo__label__box">
                                <span class="white combo__value">{{ getRange }}</span>
                            </div>
                            <h5 class="title">Advance To</h5>
                            <div class="advance__range__combo__box">
                                <span class="range__arrow">
                                    <img :src="whiteArrow" alt="White Arrow" />
                                </span>
                                <span class="combo__value" :class="{white: getRange === '3 - 4', purple: getRange != '3 - 4'}">3 - 4</span>
                                <span class="combo__value" :class="{white: getRange === '4 - 5', purple: getRange != '4 - 5'}">4 - 5</span>
                            </div>
                        </div>
                    </div>
                    <div class="examples__syllable__combinations__wrapper"
                        :class="{ disabled_tab: !(groupTargetActivation[4] || groupTargetActivation[5]) }">
                        <h6 class="title">Examples</h6>
                        <div class="examples__table__box">
                            <div class="example__table__head">
                                <label>Syllable Combo Range</label>
                                <label>Lower Range Example</label>
                                <label>Upper Range Example</label>
                            </div>
                            <div class="divider__sepline"></div>
                            <div class="example__table__row__box">
                                <div class="example__row">
                                    <div class="example__item">
                                        <span>2 - 3</span>
                                    </div>
                                    <div class="example__item">
                                        <span>Puppy</span>
                                    </div>
                                    <div class="example__item">
                                        <span>One Puppy</span>
                                    </div>
                                </div>
                                <div class="example__row">
                                    <div class="example__item">
                                        <span>3 - 4</span>
                                    </div>
                                    <div class="example__item">
                                        <span>Taco Time</span>
                                    </div>
                                    <div class="example__item">
                                        <span>it's Taco Time</span>
                                    </div>
                                </div>
                                <div class="example__row">
                                    <div class="example__item">
                                        <span>4 - 5</span>
                                    </div>
                                    <div class="example__item">
                                        <span>Peanut Butter</span>
                                    </div>
                                    <div class="example__item">
                                        <span>Some Peanut Butter</span>
                                    </div>
                                </div>
                                <div class="example__row">
                                    <div class="example__item">
                                        <span>5 - 6</span>
                                    </div>
                                    <div class="example__item">
                                        <span>Jump Over The Rocks</span>
                                    </div>
                                    <div class="example__item">
                                        <span>Eve Jumped Over The Rocks</span>
                                    </div>
                                </div>
                                <div class="example__row">
                                    <div class="example__item">
                                        <span>6 - 7</span>
                                    </div>
                                    <div class="example__item">
                                        <span>Let's Go To Grandma's House</span>
                                    </div>
                                    <div class="example__item">
                                        <span>Let's Not Go To Grandma's House</span>
                                    </div>
                                </div>
                                <div class="example__row">
                                    <div class="example__item">
                                        <span>7 - 8</span>
                                    </div>
                                    <div class="example__item">
                                        <span>I Count 7 Bananas</span>
                                    </div>
                                    <div class="example__item">
                                        <span>I Count 17 Bananas</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </template>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import api from '@/services/axios';
import { useLoaderStore } from '@/stores/loader';
import { handleError } from '@/utils/handleError';
import WorkpacketPdf from './WorkpacketPdf.vue';
import whiteArrow from '@/assets/images/white-arrow.png'

const props = defineProps({
    organizationId: Number,
    assessmentId: Number,
    learnerId: Number,
    type: {
        type: String,
        default: 'A'
    }
});

const loader = useLoaderStore();
const groupScores = ref({ 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 });
const scvAverages = ref({
    1: { syllable: '-', consonant: '-', vowel: '-' },
    2: { syllable: '-', consonant: '-', vowel: '-' },
    3: { syllable: '-', consonant: '-', vowel: '-' },
});

const groups = [
    {
        id: 1,
        color: 'white',
        labels: [
            {
                label: 'Different V Sounds',
                key: 'vowel',
            },
            {
                label: 'Different C Sounds',
                key: 'consonant',
            },
            {
                label: 'Syllables',
                key: 'syllable',
            },
        ],
    },
    {
        id: 2,
        color: 'purple',
        labels: [
            {
                label: 'Different V Sounds',
                key: 'vowel',
            },
            {
                label: 'Different C Sounds',
                key: 'consonant',
            },
            {
                label: 'Syllables',
                key: 'syllable',
            },
        ],
    },
    {
        id: 3,
        color: 'white',
        labels: [
            {
                label: 'Different V Sounds',
                key: 'vowel',
            },
            {
                label: 'Different C Sounds',
                key: 'consonant',
            },
            {
                label: 'Syllables',
                key: 'syllable',
            },
        ],
    },
];

const fetchAssessment = async () => {
    loader.show();
    try {
        const { data } = await api.get(`/organizations/${props.organizationId}/assessments/${props.assessmentId}`);
        const groupScoreData = data?.data?.result?.group_scores || {};
        const scvAverageData = data?.data?.result?.scv_averages || {};

        for (let i = 1; i <= 5; i++) {
            const scoreObj = groupScoreData[i];
            groupScores.value[i] = scoreObj?.total > 0 ? (scoreObj.score_one / scoreObj.total) * 100 : 0;

            if (scvAverageData?.[i]) {
                scvAverages.value[i] = scvAverageData?.[i];
            }
        }
    } catch (error) {
        handleError(error);
    } finally {
        loader.hide();
    }
};

const activeTargetIndex = computed(() => {
    const s = groupScores.value;

    // If Group 1 < 80 → active is 1
    if (s[1] < 80) return 1;

    // Walk through 2..5
    for (let i = 2; i <= 5; i++) {
        // If previous wasn't ≥ 80, keep last one active
        if (s[i - 1] < 80) return i - 1;

        // Previous ≥ 80 and current < 80 → current is active
        if (s[i] < 80) return i;
    }

    // All groups ≥ 80 → last one active
    return 5;
});

// Precompute activations without recursion
const groupTargetActivation = computed(() => {
    const idx = activeTargetIndex.value;
    return {
        1: idx === 1,
        2: idx === 2,
        3: idx === 3,
        4: idx === 4,
        5: idx === 5
    };
});

const getRange = computed(() => {
    const scores = groupScores.value ?? [];

    const g1 = scores[1] ?? 0;
    const g2 = scores[2] ?? 0;
    const g3 = scores[3] ?? 0;
    const g4 = scores[4] ?? 0;
    const g5 = scores[5] ?? 0;

    if (g1 >= 80 && g2 >= 80 && g3 >= 80 && g4 < 80) {
        return '2 - 3';
    }

    if (g1 >= 80 && g2 >= 80 && g3 >= 80 && g4 >= 80 && g5 < 80) {
        return '3 - 4';
    }

    if (g1 >= 80 && g2 >= 80 && g3 >= 80 && g4 >= 80 && g5 >= 80) {
        return '4 - 5';
    }

    return '  -  ';
});

onMounted(fetchAssessment);
</script>